import {
  AfterViewInit, ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject, Input,
  Output,
  ViewChild
} from '@angular/core';
import {DatePipe, NgForOf, NgIf} from "@angular/common";
import {QuarantineApiService} from "@app/quarantine/services/quarantine.api.service";
import {InputModule} from "@app/input/input.module";
import {InputComponent} from "@app/input/input.component";
import {catchError, filter, fromEvent, Observable, of, switchMap, tap} from "rxjs";
import {debounceTime, map} from "rxjs/operators";
import {
  MedicineMedicalDevicesInterface,
  MedicinesMedicalDevicesInterface,
  SelectedMedicineMedicalDevicesInterface
} from "@app/quarantine/intrfaces/quarantine.interface";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MedPaginationModule} from "@med-ui/med-pagination/med-pagination.module";
import {LoadingAnimationComponent} from "@app/loading-animation/loading-animation.component";

@Component({
  selector: 'app-create-table',
  templateUrl: './create-table.component.html',
  styleUrls: ['./create-table.component.scss'],
  standalone: true,
  imports: [
    NgForOf,
    InputModule,
    DatePipe,
    MedPaginationModule,
    NgIf,
    LoadingAnimationComponent
  ]
})
export class CreateTableComponent implements AfterViewInit {
  @Output() onSelectedChanged = new EventEmitter<SelectedMedicineMedicalDevicesInterface[]>();
  @Input() organisationId: number | undefined;
  
  @Input()
  set institutionIds (data: string[]) {
    this._institutionIds = data;
    this.getProductsList()
  }
  get institutionIds() {
    return  this._institutionIds
  }
  
  text: string = '';
  
  isLoading = false;
  
  _institutionIds: string[] = [];
  
  destroyRef = inject(DestroyRef);
  
  itemsPerPage = 20;
  currentPage = 1;
  
  @ViewChild(InputComponent, {static: true}) searchInput: InputComponent | undefined;
  @ViewChild('tableHolder', {static: true}) tableHolder: ElementRef| undefined;
  
  constructor(
      protected quarantineApiService: QuarantineApiService,
      protected changeDetectorRef: ChangeDetectorRef,
  ) {}
  
  products: MedicinesMedicalDevicesInterface = [];
  paginatedProducts: MedicinesMedicalDevicesInterface = [];
  selectedProducts: SelectedMedicineMedicalDevicesInterface[] = [];
  
  ngAfterViewInit() {
    fromEvent(this.searchInput?.inputRef.nativeElement, 'input')
        .pipe(
            // @ts-ignore
            takeUntilDestroyed(this.destroyRef),
            tap((value: string) => {
              this.products = [];
            }),
            debounceTime(500),
            map((e: any) => e?.target?.value.trim()),
            switchMap((str) => {
              if (str.length < 3) {
                return  of([]);
              }
              
              this.isLoading = true;
              this.text = str;
              let qParams: {organizationId?: [], tradeName: string} = {
                'tradeName': str,
              }
              if (this.institutionIds.length) {
                qParams['organizationId'] = this.institutionIds as any;
              }
              
              return  this.quarantineApiService.getMedicinesMedicalDevices(qParams)
            }),
            catchError((error) => {
              console.log(error);
              this.isLoading = false;
              return of([])
            })
        )
        .subscribe((value) => {
          this.isLoading = false;
          this.products = value;
          this.currentPage = 1;
          this.onPageChange(this.currentPage);
          this.onSelectedChanged.emit(this.selectedProducts);
        });
  }
  
  getProductsList() {
    if (this.text.length >= 3) {
      let qParams: {organizationId?: [], tradeName: string} = {
        'tradeName': this.searchInput?.inputRef.nativeElement?.value,
      }
      if (this.institutionIds.length) {
        qParams['organizationId'] = this.institutionIds as any;
      }
      
      this.quarantineApiService.getMedicinesMedicalDevices(qParams)
          .pipe(
              catchError((error) => {
                console.log(error);
                return of([])
              })
          )
          .subscribe((value) => {
            this.products = value;
            this.currentPage = 1;
            this.onPageChange(this.currentPage);
          });
      
    }
    
  }
    

  selectAll(e: any) {
    if (e.target.checked) {
      this.products.forEach((el: MedicineMedicalDevicesInterface) => {
        this.selectedProducts.push({
          series: el.series,
          tradeName: el.tradeName,
        });
      })
    } else {
      this.selectedProducts = [];
    }
    this.onSelectedChanged.emit(this.selectedProducts);
  }
  
  selectCurrent(el: MedicineMedicalDevicesInterface, e: any) {
    if (e.target.checked) {
      let findElement = this.selectedProducts
          .find((selectedItem: SelectedMedicineMedicalDevicesInterface) => selectedItem.series === el.series);
      if (findElement) {return} else {
        this.selectedProducts.push({series : el.series, tradeName : el.tradeName});
      }
    } else {
      let findElement = this.selectedProducts
          .find((selectedItem: SelectedMedicineMedicalDevicesInterface) => selectedItem.series === el.series);
      if (findElement) {
        this.selectedProducts.splice( this.selectedProducts.indexOf(findElement), 1);
      }
    }
    this.onSelectedChanged.emit(this.selectedProducts);
  }
  
  onPageChange(page: number) {
    this.currentPage = page;
    let startIndex = (this.currentPage - 1) * this.itemsPerPage;
    this.paginatedProducts = this.products.slice(startIndex, startIndex + this.itemsPerPage)
    this.changeDetectorRef.markForCheck()
  }
  
  isSelectedItem(item: MedicineMedicalDevicesInterface): boolean {
    return !!this.selectedProducts.find((el) => el.series === item.series)
  }
  
  get showPagination() {
    return this.products.length > 0 && this.products.length > this.itemsPerPage;
  }
  
  get isAllSelected() {
    if (this.products.length === 0) {return false}
    return this.selectedProducts.length === this.products.length;
  }
  
  get countItemsPerPage() {
    return Math.floor((this.tableHolder?.nativeElement.offsetHeight - 104)/72);
  }
  
}
